import React, { Fragment, useRef, useState, useContext, useEffect } from 'react';
import { AuthContext } from "../contexts/auth";
import { Dialog, Transition } from '@headlessui/react';
import ComboboxAutocomplete from './combobox_autocomplete';
import { listOrganizationalUnits } from "../services/api_org_unit";
import { getWorkingHoursCompound, getWeeklyWorkingHourCompound } from '../services/api_working_hours';
import moment from 'moment';
import Toggle from './toggle';
import PairOrgUnit from './pair_org_unit';
import { AddDailyWorkingHour } from './add_daily_working_hour_calendar';
import TabellaTurnoGiornaliero from './tabella_visualizza_turno giornaliero';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import { BriefcaseIcon, PlusCircleIcon, ChevronRightIcon, ChevronLeftIcon, TrashIcon, PencilSquareIcon, InformationCircleIcon, NoSymbolIcon, PlusIcon, ExclamationTriangleIcon, ClipboardDocumentCheckIcon } from '@heroicons/react/24/outline';
import { getCalendarWorkingHour, createEmployeeWorkingHour, deleteEmployeeDailyWorkingHour, updateEmployeeDailyWorkingHour, getCalendarTotalHours, updateEmployeeWorkingHourStatus, updateEmployeeDWHOuPair } from '../services/api_calendar';
import Swal from 'sweetalert2';
import { convertWorkingHourToShifts } from '../utils/timeslots';
import { Link } from 'wouter';



const ModalSelectCreateShift = ({ onClose, selectedDay, selectedEmployee, onAssignNewShift, allowHiddenBreaks }) => {
    const { token } = useContext(AuthContext);
    const cancelButtonRef = useRef(null);
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [tempSelectedUnit, setTempSelectedUnit] = useState(null);
    const [units, setUnits] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [isError, setIsError] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);
    const [workingHours, setWorkingHours] = useState([]);
    const [selectedWorkingHourId, setSelectedWorkingHourId] = useState(null);
    const [selectedWeeklyWorkingHourId, setSelectedWeeklyWorkingHourId] = useState(null);
    const [temporarySelectedUnit, setTemporarySelectedUnit] = useState(selectedUnit || null);
    const [employeeWorkingHours, setEmployeeWorkingHours] = useState([]);

    
    const handleToggleChange = (selectedOption) => {
        setSelectedOption(selectedOption);
    };



    useEffect(() => {
        const fetchWorkingHours = async () => {
            try {
                const workingHoursData = await getWorkingHoursCompound(token, selectedUnit?.id, 'shifts');
                setWorkingHours(workingHoursData || []);
            } catch (error) {
                console.error('Errore durante il recupero del turno', error);
            }
        };
    
        fetchWorkingHours();
    }, [token, selectedUnit]);

    const selectedWorkingHour = Array.isArray(workingHours) 
        ? workingHours.find(wh => wh.id === selectedWorkingHourId) 
        : null;

    useEffect(() => {
        const fetchUnits = async () => {
            setIsLoading(true);
            setIsError(false);
    
            try {
                const response = await listOrganizationalUnits(token, true, null, 'shifts');

                setUnits(response.data || [] );
            } catch (error) {
                console.error('Error fetching units:', error);
                {(error?.response?.status !== 402) &&
                    setIsError(true);
                    setIsLoading(false);
                }
            } finally {
                setIsLoading(false);
            }
        };
    
        fetchUnits();
    }, [token]);
    
    
    const handleSaveWorkingHour = async (shiftPreviews) => {
        let workingHourData;
        const formattedDate = moment(selectedDay).format('DD/MM/YYYY');       

        if (selectedOption === "CREA FASCIA ORARIA") {
            // Creazione di una nuova fascia oraria
            workingHourData = {
                date: selectedDay,
                working_hour: null,
                time_slots: shiftPreviews.map(preview => ({
                    start_at: preview.workStartTime,
                    end_at: preview.workEndTime,
                    breaks: preview.breaks.map(b => ({
                        start_at: b.start_at,
                        end_at: b.end_at,
                        is_visible: b.is_visible
                    }))
                })),
                ou: temporarySelectedUnit?.id,
                name: temporarySelectedUnit?.name,
                typology: 'custom',
            };
        } else {
            // Selezione di una fascia oraria esistente
            const selectedWorkingHour = workingHours.find(wh => wh.id === selectedWorkingHourId);
    
            if (!selectedWorkingHour) {
                console.error('Fascia oraria non trovata');
                return;
            }
    
            workingHourData = {
                date: selectedDay,
                working_hour: selectedWorkingHour.id,
                time_slots: [],
                ou: selectedUnit?.id,
                name: selectedUnit?.name,
            };
        }
    
        try {
            if (selectedEmployee !== undefined) {

                Swal.fire({
                    title: 'Assegnazione turno...',
                    icon: 'info',
                    showConfirmButton: false,
                    allowOutsideClick: false,
                    allowEscapeKey: false,
                    didOpen: () => {
                        Swal.showLoading();
                    }
                });               
                
                const responseDWH = await createEmployeeWorkingHour(token, selectedEmployee?.id, workingHourData);
    
                const time_slots = responseDWH.working_hour ? convertWorkingHourToShifts(responseDWH.working_hour, workingHours) : responseDWH.time_slots;
                // crea nuovo turno con typology CUSTOM se era stata selezionata l'opzione crea fascia oraria
                const isCustom = selectedOption === "CREA FASCIA ORARIA";

    
                const indexToUpdate = employeeWorkingHours.findIndex(entry => entry.employeeId === selectedEmployee && entry.date === responseDWH.date);
                
                const newShift = {
                    ref_name: responseDWH.ref_name,
                    ref_ou: responseDWH.ou,
                    dailyWorkingHourId: responseDWH.id,
                    typology: isCustom ? 'custom' : responseDWH.typology,
                    time_slots: time_slots,
                }

                // Crea un nuovo array con il dato aggiornato
                const updatedEmployeeWorkingHours = [...employeeWorkingHours];
                if (indexToUpdate !== -1) {
                    updatedEmployeeWorkingHours[indexToUpdate] = {
                        ...updatedEmployeeWorkingHours[indexToUpdate],
                        dailyWorkingHourId: responseDWH.id,
                        typology: 'turno',
                        time_slots: time_slots,
                        ref_name: responseDWH.ref_name,
                        ref_ou: responseDWH.ref_ou,
                        shifts: [...updatedEmployeeWorkingHours[indexToUpdate]?.shifts, newShift],
                    };
                } else {
                    // Se il giorno selezionato non è già presente, aggiungilo all'array
                    updatedEmployeeWorkingHours.push({
                        employeeId: selectedEmployee,
                        date: responseDWH.date,
                        dailyWorkingHourId: responseDWH.id,
                        typology: 'turno',
                        time_slots: time_slots,
                        ref_ou: workingHourData.ou,
                        shifts: [newShift],
                    });
                }
    
                setEmployeeWorkingHours(updatedEmployeeWorkingHours);

                
                
                setSelectedOption('');
                setSelectedWorkingHourId(null);
                setSelectedWeeklyWorkingHourId(null);
                // setModalOpen(false);
                if (selectedUnit) 
                    setTemporarySelectedUnit(selectedUnit);
                else setTemporarySelectedUnit('');

                // chiudi lo spinner prima del success
                Swal.close();

                // mostra il successo e attendi la conferma
                const { isConfirmed } = await Swal.fire({
                    position: "center",
                    icon: "success",
                    title: "Turno creato con successo!",
                    text: "Procedi con la conferma per finalizzare l'assegnazione.",
                    showConfirmButton: true,
                    confirmButtonText: "OK"
                });

                // chiudi la modale SOLO se l’utente conferma
                if (isConfirmed) {
                    onClose(); // chiude la modale del turno
                    onAssignNewShift();
                }
            } else {
                throw new Error('Nessun dipendente selezionato');
            }
        } catch (error) {
            console.error('Errore durante l\'assegnazione del turno:', error);
            let errorMessage = 'Si è verificato un errore durante l\'assegnazione del turno. Per favore riprova.';
        
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                const errorMessages = [];
        
                Object.keys(errorData).forEach((key) => {
                    const messageArray = errorData[key];
                    if (Array.isArray(messageArray)) {
                        errorMessages.push(...messageArray);
                    } else if (typeof messageArray === 'string') {
                        errorMessages.push(messageArray);
                    }
                });
        
                if (errorMessages.length > 0) {
                    errorMessage = errorMessages.join(' ');
                }
            }
        
            Swal.fire({
                icon: 'error',
                title: 'Errore',
                text: errorMessage,
                confirmButtonText: 'OK',
            });
        }
    };

    const handleTemporarySelectUnit = (selectedUnit) => {
        setTemporarySelectedUnit(() => selectedUnit);
    };

    const handleResetOUtoPairSelection = () => {
        setSelectedOuToPair(null);
    };

    const handleSelectUnit = (selected) => {
        const unit = selected === 'all' || !selected ? null : selected;
        setSelectedUnit(unit);
    };

    // Se ti serve, decommenta e usa questa funzione:
    // const handleConfirmUnit = () => {
    //   setSelectedUnit(tempSelectedUnit);
    //   onSelectOUtoPair(tempSelectedUnit);
    //   onClose();
    // };

    return (
        <Transition.Root show={true} as={Fragment}>
            <Dialog
                as="div"
                className="fixed inset-0 overflow-y-auto"
                onClose={() => {}}
                initialFocus={cancelButtonRef}
                style={{ zIndex: '900' }}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </Transition.Child>

                <div className="fixed inset-0 w-screen overflow-y-auto" style={{ zIndex: '300' }}>
                    <div className="flex h-auto md:items-top mt-16 justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-gray-50 shadow-xl transition-all my-8 w-3/4 2xl:w-2/4 py-4 px-4">
                                <div className="flex justify-end">
                                    <button
                                        type="button"
                                        className="border shadow text-gray-700 text-sm w-24 px-2 py-1 rounded-md hover:bg-gray-200 focus:outline-none font-semibold uppercase bg-white"
                                        onClick={onClose}
                                    >
                                    Chiudi
                                    </button>
                                </div>
                                {selectedDay && (
                                    <div className="w-full flex justify-center my-4">
                                        <div className="w-full">
                                            <Toggle
                                                color="indigo"
                                                gradeBgColor="600"
                                                optionLeft="ASSEGNA TURNO GIORNALIERO"
                                                optionRight="CREA FASCIA ORARIA"
                                                selectedOption={selectedOption}
                                                onChange={handleToggleChange}
                                            />
                                        </div>
                                    </div> 
                                )}
                                {selectedDay && selectedOption === "CREA FASCIA ORARIA" && (
                                    <>
                                        <PairOrgUnit 
                                            onSelectOrganizationalUnit={handleTemporarySelectUnit}           
                                            selectedOU={selectedUnit}
                                            onResetOrganizationalUnit={handleResetOUtoPairSelection}
                                        />
                                        <AddDailyWorkingHour 
                                            selectedDay={selectedDay} 
                                            onSaveDailyWorkingHour={handleSaveWorkingHour}
                                            allowHiddenBreaks={allowHiddenBreaks}
                                        />
                                    </>
                                )}

                                {selectedDay && selectedOption === "ASSEGNA TURNO GIORNALIERO" && (
                                    <div className='container bg-white py-4 border rounded-md shadow-md'>
                                        <div className='grid grid-cols-1 px-8 mb-4 w-full'>
                                            <div className='flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full'>
                                                <div className='w-48'>
                                                    <span className='text-sm font-medium text-gray-700'>Visualizza turni di</span>
                                                </div>
                                                {isLoading ? (
                                                    "Caricamento..."
                                                ) : (
                                                    <div className="w-full border border-gray-200 rounded-lg">
                                                        <ComboboxAutocomplete
                                                            options={[
                                                                { id: "__ALL__", name: "Tutti (nessuna unità)" },
                                                                ...(units || []),
                                                            ]}
                                                            bgActiveColor="bg-indigo-100"
                                                            textActiveColor="text-gray-900"
                                                            placeholder="Seleziona Unità Organizzativa..."
                                                            onChange={(value) => {
                                                                if (!value || value.id === "__ALL__") {
                                                                handleSelectUnit(null);
                                                                } else {
                                                                handleSelectUnit(value);
                                                                }
                                                            }}
                                                            value={selectedUnit ?? { id: "__ALL__", name: "Tutti (nessuna unità)" }}
                                                            positionOptions="relative"
                                                            borderClass="border-gray-200 shadow-md"
                                                            alwaysFirstIds={["__ALL__"]}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <div className='grid grid-cols-1 px-8 mb-4 w-full'>
                                            {workingHours.length > 0 ? (
                                                <div className='flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 w-full'>
                                                    <div className='w-48'>
                                                        <span className='text-sm font-medium text-gray-700'>Assegna Turno Giornaliero</span>
                                                    </div>
                                                    <div className='w-full border border-gray-200 rounded-lg'>
                                                        <ComboboxAutocomplete
                                                            options={workingHours}
                                                            bgActiveColor="bg-indigo-100"
                                                            textActiveColor="text-gray-900"
                                                            placeholder="Seleziona turno giornaliero..."
                                                            onChange={(value) => setSelectedWorkingHourId(value?.id ?? null)}
                                                            value={workingHours.find(wh => wh.id === selectedWorkingHourId) || null}
                                                            positionOptions='relative'
                                                            borderClass='border-gray-100 shadow-md'
                                                        />
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className='col-span-6 flex justify-center py-5 items-center w-full rounded-lg border-2 border-dashed border-gray-300 text-sm font-semibold text-gray-900'> 
                                                    <div className="text-center p-5">
                                                        <p>Nessun turno giornaliero presente</p>
                                                        <p>Vai alla pagina di <Link to="/shift_management/create?type=giornaliero"><span className='underline text-indigo-500 uppercase cursor-pointer'>creazione turno</span></Link></p>
                                                    </div>
                                                </div>
                                            )}
                                            
                                            <div className='col-span-6'>
                                                {selectedWorkingHour && (
                                                    <TabellaTurnoGiornaliero selectedWorkingHour={selectedWorkingHour} />
                                                )}
                                                
                                                {selectedWorkingHour && (
                                                    <div className='col-span-1 flex justify-center mt-4'>
                                                        <button
                                                            className="py-2 bg-indigo-600 rounded-md mb-2 w-28 text-white hover:bg-indigo-500 font-semibold text-sm uppercase me-2"
                                                            onClick={handleSaveWorkingHour}
                                                        >
                                                            Salva
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
};

export default ModalSelectCreateShift;